import {
  MutationFunction,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { UUID } from 'crypto'

import * as API from '../../api/applicationsAPI'
import {
  Application,
  ApplicationTrackingData,
  ApplicationSummary,
  RegenerateAnswerFn,
  RegeneratedAnswer,
  SaveAnswerFn,
} from '../../models/Applications'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { useAuth0 } from '@/utils/auth/auth0'

// Generic mutation hook for applications
function useApplicationMutation<TData = unknown, TVariables = unknown>(
  apiClientFn: MutationFunction<TData, TVariables>,
  orgUuid: UUID | null,
  needId?: number | null,
  appId?: number
) {
  const queryClient = useQueryClient()
  const { user } = getCurrentUser()

  return useMutation({
    mutationFn: apiClientFn,
    onSuccess: () => {
      // Invalidate all applications queries when any mutation succeeds
      if (orgUuid) {
        queryClient.invalidateQueries({
          queryKey: ['applications', 'summary', orgUuid],
        })
        if (needId) {
          queryClient.invalidateQueries({
            queryKey: ['applications', 'need', needId, orgUuid],
          })
        }
      }

      // Invalidate specific application
      if (appId) {
        queryClient.invalidateQueries({
          queryKey: ['application', appId],
        })
      }

      // Also invalidate onboarding data since applications affect onboarding status
      if (user?.uuid) {
        queryClient.invalidateQueries({
          queryKey: ['onboardingData', user.uuid, false, orgUuid],
        })
      }
    },
  })
}

// Individual mutation hooks
export function useCreateApplicationMutation(orgUuid: UUID | null) {
  return useApplicationMutation(API.createApplication, orgUuid)
}

export function useUpdateQuestionsMutation(
  orgUuid: UUID | null,
  appId?: number
) {
  return useApplicationMutation(
    API.updateApplicationQuestions,
    orgUuid,
    null,
    appId
  )
}

export function useRegenerateAnswerMutation(
  orgUuid: UUID | null,
  appId?: number
) {
  return useApplicationMutation<RegeneratedAnswer, RegenerateAnswerFn>(
    API.regenerateAnswer,
    orgUuid,
    undefined,
    appId
  )
}

export function useSaveAnswerMutation(orgUuid: UUID | null, appId?: number) {
  return useApplicationMutation<void, SaveAnswerFn>(
    API.saveAnswer,
    orgUuid,
    undefined,
    appId
  )
}

export function useAddApplicationMutation(
  orgUuid: UUID | null,
  needId?: number
) {
  return useApplicationMutation(API.addApplicationForTracking, orgUuid, needId)
}

export function useUpdateApplicationTrackingMutation(
  needId?: number,
  orgUuid?: UUID | null,
  appId?: number
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: API.updateApplicationTrackingData,
    onSuccess: () => {
      // Invalidate need-specific queries
      if (needId && orgUuid) {
        queryClient.invalidateQueries({
          queryKey: ['applications', 'need', needId, orgUuid],
        })
      }
      // Invalidate summary for the org
      if (orgUuid) {
        queryClient.invalidateQueries({
          queryKey: ['applications', 'summary', orgUuid],
        })
      }

      // Invalidate specific application
      if (appId) {
        queryClient.invalidateQueries({
          queryKey: ['application', appId],
        })
      }
    },
  })
}

// Main hook that combines query and mutations
export function useApplications(
  token: string | null,
  orgUuid: UUID | null,
  needId: number | null,
  appId?: number
) {
  const { getAccessTokenSilently } = useAuth0()

  const query = useQuery<Application[]>({
    queryKey: ['applications', orgUuid],
    queryFn: async () => {
      const freshToken = await getAccessTokenSilently()
      return API.getApplicationsByOrg(freshToken, orgUuid!)
    },
    enabled: !!token && !!orgUuid,
  })

  return {
    ...query,
    data: Array.isArray(query.data) ? query.data : undefined,
    createApplication: useCreateApplicationMutation(orgUuid),
    regenerateAnswer: useRegenerateAnswerMutation(orgUuid, appId),
    saveAnswer: useSaveAnswerMutation(orgUuid, appId),
    updateApplicationTracking: useUpdateApplicationTrackingMutation(
      needId ?? undefined,
      orgUuid,
      appId ?? undefined
    ),
    updateApplicationQuestions: useUpdateQuestionsMutation(
      orgUuid,
      appId ?? undefined
    ),
  }
}

// 3. Get all applications for a need with tracking data
export function useApplicationsByNeed(
  token: string | null,
  orgUuid: UUID | null,
  needId: number | null
) {
  const { getAccessTokenSilently } = useAuth0()

  const query = useQuery<ApplicationTrackingData[]>({
    queryKey: ['applications', 'need', needId, orgUuid],
    queryFn: async () => {
      const freshToken = await getAccessTokenSilently()
      return API.getApplicationsByNeed(freshToken, needId!, orgUuid!)
    },
    enabled: !!token && !!orgUuid && !!needId,
  })

  return {
    ...query,
    data: Array.isArray(query.data) ? query.data : undefined,
  }
}

// get single application
export function useApplication(
  token: string | null,
  applicationId: number | null,
  orgUuid: UUID | null
) {
  const { getAccessTokenSilently } = useAuth0()

  return useQuery<ApplicationTrackingData | null>({
    queryKey: ['application', applicationId],
    queryFn: async () => {
      const freshToken = await getAccessTokenSilently()
      return API.getApplication(freshToken, applicationId!, orgUuid!)
    },
    enabled: !!token && !!applicationId && !!orgUuid,
  })
}

// get application summary data for org
export function useApplicationsSummary(
  token: string | null,
  orgUuid: UUID | null
) {
  const { getAccessTokenSilently } = useAuth0()

  const query = useQuery<ApplicationSummary[]>({
    queryKey: ['applications', 'summary', orgUuid],
    queryFn: async () => {
      const freshToken = await getAccessTokenSilently()
      return API.getApplicationsSummary(freshToken, orgUuid!)
    },
    enabled: !!token && !!orgUuid,
  })

  return {
    ...query,
    data: Array.isArray(query.data) ? query.data : undefined,
  }
}

// Optional: Type for the return value of useApplications
export type UseApplicationsReturn = ReturnType<typeof useApplications>
